import { Subscription, interval } from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RealtimeService } from '../../../services/realtime.service';
import { Viewer } from './models/viewer.model';
import { ChartDataset } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { PublicationService } from 'app/services/publication.service';

@Component({
	selector: 'realtime-audience-graph',
	templateUrl: './realtime-audience-graph.component.html',
	styleUrls: ['./realtime-audience-graph.component.scss']
})

export class RealtimeAudienceGraphComponent implements OnInit, OnDestroy {
	@ViewChild('chart') chart: any;

	chartOptions: any;
	realtimeGraphDatasets: ChartDataset[];
	updateInterval: number;
	xAxisLabels: string[];

	private viewerCount: number;
	private viewerCountMobile: number;
	private timeStamps: number[];
	private intervalSubscription: Subscription;
	private showSeconds: boolean;

	private initialCallFinishedSubscription: Subscription;

	private viewerAdded: Subscription;
	private viewerRemoved: Subscription;
	private viewerCountsSubscription: Subscription;
	private usePolling: boolean;
	private locale: string;


	constructor(private realtimeService: RealtimeService, private translate: TranslateService, private publicationService: PublicationService) {
		// usePolling will poll the backend instead of depending on frontend viewerSessions
		this.usePolling = true;

		this.viewerCount = 0;
		this.viewerCountMobile = 0;
		this.updateInterval = 10;
		this.timeStamps = [];
		this.xAxisLabels = [];
		this.showSeconds = true;
		this.resetGraph();
		this.locale = this.publicationService.locale;
	}

	resetGraph() {
		this.realtimeGraphDatasets = [];

		['VIEWER_TOTALS', 'AVERAGE', 'MOBILE'].forEach((dataLine: string) => {
			const dataSet: ChartDataset = {
				label: this.translate.instant(dataLine),
				data: [],
				borderWidth: 1
			};

			switch (dataLine) {
				case 'VIEWER_TOTALS':
					dataSet.backgroundColor = '#7AB1DF';
					dataSet.borderColor = '#7AB1DF';
					break;
				case 'AVERAGE':
					dataSet.backgroundColor = '#8AAECC';
					dataSet.borderColor = '#8AAECC';
					break;
				case 'MOBILE':
					dataSet.backgroundColor = '#C6D5E1';
					dataSet.borderColor = '#C6D5E1';
					break;
			}

			this.realtimeGraphDatasets.push(dataSet);
		});

	}

	ngOnInit() {
		if (!this.usePolling) {
			if (!this.intervalSubscription) {
				this.initialCallFinishedSubscription = this.realtimeService.initialCallFinished
					.subscribe(() => {
						this.intervalSubscription = interval(500).subscribe((interval2) => {
							if (interval2 % this.updateInterval === 0) {
								this.updateGraph();
							}
						});
					});
			}

			this.realtimeService.lostFocus.subscribe((lostFocus: boolean) => {
				if (lostFocus) {
					if (this.intervalSubscription) {
						this.viewerCount = 0;
						this.viewerCountMobile = 0;
						this.resetGraph();
						if (this.intervalSubscription) {
							this.intervalSubscription.unsubscribe();
							this.intervalSubscription = null;
						}
						if (this.viewerAdded) {
							this.viewerAdded.unsubscribe();
							this.viewerAdded = null;
						}
						if (this.viewerRemoved) {
							this.viewerRemoved.unsubscribe();
							this.viewerRemoved = null;
						}
					}
				} else {
					this.subscribeViewers();
				}
			});
		}

		this.subscribeViewers();
	}

	ngOnDestroy() {
		if (this.initialCallFinishedSubscription) {
			this.initialCallFinishedSubscription.unsubscribe();
		}
		if (this.intervalSubscription) {
			this.intervalSubscription.unsubscribe();
		}
		if (this.viewerCountsSubscription) {
			this.viewerCountsSubscription.unsubscribe();
		}
	}

	subscribeViewers() {
		if (this.usePolling) {
			if (!this.viewerCountsSubscription) {
				this.viewerCountsSubscription = this.realtimeService.viewerCountsObs.subscribe((viewerCounts: any) => {
					if (viewerCounts) {
						this.viewerCount = viewerCounts.viewers || 0;
						this.viewerCountMobile = viewerCounts.mobileViewers || 0;
						this.updateGraph();
					}
				});
			}
		} else {
			if (!this.viewerAdded) {
				this.viewerAdded = this.realtimeService.viewerAdded
					.subscribe((viewer: Viewer) => {
						// add
						if (!viewer.isRemoved) {
							this.viewerCount++;
							if (viewer.isMobile) {
								this.viewerCountMobile++;
							}
						}
					}
					);
			}
			if (!this.viewerRemoved) {
				this.viewerRemoved = this.realtimeService.viewerRemoved
					.subscribe((viewer: Viewer) => {
						// remove
						if (this.viewerCount > 0) {
							this.viewerCount--;
						}
						if (viewer.isMobile) {
							if (this.viewerCountMobile > 0) {
								this.viewerCountMobile--;
							}
						}
					}
					);
			}
		}
	}

	updateGraph() {
		const viewers = this.realtimeGraphDatasets[0].data as number[];
		const mobileViewers = this.realtimeGraphDatasets[2].data as number[];

		if (this.timeStamps.length > 1) {
			let difference: number = this.timeStamps[this.timeStamps.length - 1] - this.timeStamps[0];
			if (this.showSeconds && difference > 5 * 60 * 1000) {
				// data > 5m, change format to hh:mm
				this.showSeconds = false;
				this.xAxisLabels = this.xAxisLabels.map(label => label.slice(0, 5));
			}
			// data older > 15m: remove
			while (difference > 15 * 60 * 1000) {
				this.timeStamps.shift();
				this.xAxisLabels.shift();
				viewers.shift();
				mobileViewers.shift();
				difference = this.timeStamps[this.timeStamps.length - 1] - this.timeStamps[0];
			}
		}

		// create X axis labels as current timestamps
		const now: number = new Date().getTime();
		this.timeStamps.push(now);
		const xAxisTime: string = Intl.DateTimeFormat(this.locale, this.getDateFormat(this.showSeconds)).format(now);
		this.xAxisLabels.push(xAxisTime);

		viewers.push(this.viewerCount);
		mobileViewers.push(this.viewerCountMobile);

		const sum = viewers.reduce((acc, current) => acc + current, 0);

		const average = (sum / viewers.length);

		// average: same value for each X axis label
		this.realtimeGraphDatasets[1].data = [];
		this.xAxisLabels.forEach((stat) => {
			this.realtimeGraphDatasets[1].data.push(average);
		});

		this.realtimeGraphDatasets = [...this.realtimeGraphDatasets];
	}

	getDateFormat(showSeconds: boolean): Intl.DateTimeFormatOptions {
		if (showSeconds) {
			return {
				hour: 'numeric',
				minute: 'numeric',
				second: 'numeric'
			};
		}
		return {
			hour: 'numeric',
			minute: 'numeric',
		};
	}
}
